import { useState } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { AdminPageSkeleton } from '../components/AdminPageSkeleton'
import { AdminPageLayout } from '../components/AdminPageLayout'
import { useAppointmentById, useCancelByAdmin, useNoShow } from '@/api/hooks/appointments.hooks'
import { getVisitTypeLabel } from '@/api/enums'
import { formatCents } from '@/utils/cancellation-policy'
import { toastCallbacks } from '@/api/hooks/useMutationWithToast'
import { AppointmentDetailsCard, PatientInfoCard, ProviderInfoCard, ReasonCard } from './components/AppointmentInfoCards'
import { ActionPanel } from './components/ActionPanel'
import { RescheduleModal } from './components/RescheduleModal'
import { NewDoctorModal } from './components/NewDoctorModal'
import { GeneratePaymentLinkModal } from './components/GeneratePaymentLinkModal'

export default function ManageAppointment() {
  const { id } = useParams({ from: '/admin/appointment/$id' })
  const navigate = useNavigate()

  const { data: appointment, isLoading, error } = useAppointmentById(id)

  const cancelByAdmin = useCancelByAdmin()
  const noShowMut = useNoShow()

  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false)
  const [isCancelOpen, setIsCancelOpen] = useState(false)
  const [isNoShowOpen, setIsNoShowOpen] = useState(false)
  const [isNewDoctorOpen, setIsNewDoctorOpen] = useState(false)
  const [isPaymentLinkOpen, setIsPaymentLinkOpen] = useState(false)

  const onBack = () => navigate({ to: '/admin' })

  if (isLoading) return <AdminPageSkeleton variant='manageAppointment' />
  if (error || !appointment) {
    return (
      <div className='container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-6 py-8'>
        <h2 className='mb-2 text-2xl font-semibold'>Appointment Not Found</h2>
        <p className='mb-4 text-muted-foreground'>The requested appointment could not be found.</p>
        <Button onClick={onBack}><ArrowLeft className='mr-2 h-4 w-4' />Back to Dashboard</Button>
      </div>
    )
  }

  const visitTypeLabel = getVisitTypeLabel(appointment.visitType)
  // An admin cancellation ignores the notice window and refunds in full, so
  // what the patient gets back is whatever settled, not the policy's
  // "refund if cancelled early" figure.
  const refundOnCancelCents =
    appointment.cancellationPolicy.refundAmountCents +
    appointment.cancellationPolicy.retainedAmountCents
  const headerAside = (
    <>
      <StatusBadge status={appointment.status} type='primary' />
      {visitTypeLabel && <Badge variant='outline' className='text-xs font-medium'>{visitTypeLabel}</Badge>}
      {appointment.durationInMinutes ? <Badge variant='outline' className='text-xs font-medium'>{appointment.durationInMinutes} min</Badge> : null}
    </>
  )

  return (
    <>
      <AdminPageLayout title='Manage Appointment' subtitle={`Appointment #${appointment.id.toUpperCase()}`} backLabel='Back to Dashboard' onBack={onBack} headerAside={headerAside}>
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-12'>
          <div className='space-y-6 lg:col-span-4'>
            <AppointmentDetailsCard appointment={appointment} />
            <ProviderInfoCard appointment={appointment} />
          </div>
          <div className='space-y-6 lg:col-span-4'>
            <PatientInfoCard appointment={appointment} />
            <ReasonCard appointment={appointment} />
          </div>
          <div className='lg:col-span-4'>
            <ActionPanel
              appointment={appointment}
              onOpenReschedule={() => setIsRescheduleOpen(true)}
              onOpenCancel={() => setIsCancelOpen(true)}
              onOpenNoShow={() => setIsNoShowOpen(true)}
              onOpenNewDoctor={() => setIsNewDoctorOpen(true)}
              onOpenPaymentLink={() => setIsPaymentLinkOpen(true)}
            />
          </div>
        </div>
      </AdminPageLayout>

      {isRescheduleOpen && (
        <RescheduleModal
          appointmentId={appointment.id}
          specialistSlug={appointment.specialistSlug}
          onClose={() => setIsRescheduleOpen(false)}
        />
      )}

      <ConfirmModal
        isOpen={isCancelOpen}
        onClose={() => setIsCancelOpen(false)}
        title='Cancel Appointment'
        body={
          <>
            <p className='text-sm text-muted-foreground'>Canceling will remove this appointment from the schedule immediately.</p>
            {/* An UberDoc-side cancellation always refunds in full, whatever the
                notice, so the admin sees the amount before committing. */}
            {refundOnCancelCents > 0 ? (
              <p className='rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800'>
                This refunds <span className='font-semibold'>{formatCents(refundOnCancelCents)}</span> to the patient, and emails them to say so.
              </p>
            ) : (
              <p className='rounded-lg border border-border bg-muted p-3 text-sm text-muted-foreground'>
                No fee was charged for this appointment, so nothing will be refunded.
              </p>
            )}
            <p className='text-sm font-medium text-foreground'>This cannot be undone.</p>
          </>
        }
        primaryLabel='Cancel Appointment'
        onPrimary={() => {
          cancelByAdmin.mutate(
            { appointmentId: appointment.id },
            toastCallbacks({ onSuccess: 'Cancelled', onSuccessFn: () => setIsCancelOpen(false) })
          )
        }}
        secondaryLabel='Keep Appointment'
      />

      <ConfirmModal
        isOpen={isNoShowOpen}
        onClose={() => setIsNoShowOpen(false)}
        title='Mark as No-Show'
        body={
          <>
            {/* Nothing further is charged here. The $50 taken at confirmation is
                simply kept, which is what the no-show fee is. */}
            <p className='text-sm text-muted-foreground'>The $50 approval fee already charged is kept as the no-show fee. The patient is not charged anything further and is not refunded.</p>
            <p className='text-sm font-medium text-foreground'>This cannot be undone.</p>
          </>
        }
        primaryLabel='Mark as No-Show'
        onPrimary={() => {
          noShowMut.mutate(
            { appointmentId: appointment.id },
            toastCallbacks({ onSuccess: 'Marked no-show', onSuccessFn: () => setIsNoShowOpen(false) })
          )
        }}
        secondaryLabel='Keep Appointment'
      />

      {isNewDoctorOpen && (
        <NewDoctorModal appointment={appointment} onClose={() => setIsNewDoctorOpen(false)} />
      )}

      {isPaymentLinkOpen && (
        <GeneratePaymentLinkModal appointmentId={appointment.id} onClose={() => setIsPaymentLinkOpen(false)} />
      )}
    </>
  )
}
